import { parseSquare, squareName } from './board.js';
import { RuleError } from './errors.js';
import { viewFor } from './fog.js';
import { legalActions, sameAction } from './moves.js';
import type { Action, GameState, PieceAction, PieceType } from './types.js';

/**
 * Notation of an action by a piece in the given state (the state before the action):
 * `Ce2-e4` (move), `Xb9xc7` (capture), `Lc3 pin c6` (pin), `Mf4 +g5` / `Mf4 -g5` (build).
 */
export function toNotation(state: GameState, pieceId: string, action: Action): string {
  const piece = state.pieces.find((p) => p.id === pieceId);
  if (piece === undefined) throw new RuleError('UNKNOWN_PIECE', `No piece with id ${pieceId}`);
  const from = `${piece.type}${squareName(piece.r, piece.c)}`;
  const to = squareName(action.r, action.c);
  switch (action.kind) {
    case 'move':
      return `${from}-${to}`;
    case 'capture':
      return `${from}x${to}`;
    case 'pin':
      return `${from} pin ${to}`;
    case 'build':
      return `${from} ${action.delta > 0 ? '+' : '-'}${to}`;
  }
}

const PATTERN =
  /^([BCXLM])([a-i][1-9])(?:-([a-i][1-9])|x([a-i][1-9])| pin ([a-i][1-9])| ([+-])([a-i][1-9]))$/;

/**
 * Parses notation for the side to move and returns the matching legal action. Throws a
 * `RuleError` with code `BAD_NOTATION` for unreadable text and `ILLEGAL_ACTION` when the
 * action is not legal in this state.
 */
export function fromNotation(state: GameState, text: string): PieceAction {
  const match = PATTERN.exec(text.trim());
  if (!match) throw new RuleError('BAD_NOTATION', `Unreadable move: "${text}"`);
  const [, type, fromName, moveTo, captureTo, pinAt, sign, buildAt] = match;
  const from = parseSquare(fromName ?? '');
  const piece = state.pieces.find(
    (p) =>
      p.alive &&
      p.team === state.turn &&
      p.type === (type as PieceType) &&
      p.r === from.r &&
      p.c === from.c,
  );
  if (piece === undefined) {
    throw new RuleError(
      'ILLEGAL_ACTION',
      `No ${type ?? ''} of the side to move on ${fromName ?? ''}`,
    );
  }

  let wanted: (a: Action) => boolean;
  if (moveTo !== undefined) {
    const to = parseSquare(moveTo);
    wanted = (a) => a.kind === 'move' && a.r === to.r && a.c === to.c;
  } else if (captureTo !== undefined) {
    const to = parseSquare(captureTo);
    wanted = (a) => a.kind === 'capture' && a.r === to.r && a.c === to.c;
  } else if (pinAt !== undefined) {
    const to = parseSquare(pinAt);
    wanted = (a) => a.kind === 'pin' && a.r === to.r && a.c === to.c;
  } else {
    const to = parseSquare(buildAt ?? '');
    const delta = sign === '+' ? 1 : -1;
    wanted = (a) => a.kind === 'build' && a.r === to.r && a.c === to.c && a.delta === delta;
  }

  // Under fog a player chooses from what they can see.
  const action = legalActions(viewFor(state, state.turn), piece.id).find(wanted);
  if (action === undefined) throw new RuleError('ILLEGAL_ACTION', `Illegal move: "${text}"`);
  return { pieceId: piece.id, action };
}

/** Whether `action` appears in `actions`. */
export function containsAction(actions: readonly Action[], action: Action): boolean {
  return actions.some((a) => sameAction(a, action));
}
